import { useState, useEffect } from "react";
import { customerApi } from "@/services/api";
import { toast } from "sonner";
import { Users, Plus, X, Loader2, Phone, Mail, ChevronRight } from "lucide-react";

export default function Customers() {
  const [customers, setCustomers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [showCreate, setShowCreate] = useState(false);
  const [selected, setSelected] = useState(null);

  const load = () => {
    customerApi.listCustomers().then(r => setCustomers(r.data)).finally(() => setLoading(false));
  };

  useEffect(() => { load(); }, []);

  if (loading) return <div className="p-6 animate-pulse space-y-4"><div className="h-8 bg-[#F3F3F3] rounded-lg w-64"/><div className="h-40 bg-[#F3F3F3] rounded-xl"/></div>;

  return (
    <div className="p-6 max-w-5xl mx-auto space-y-6">
      <div className="flex items-start justify-between">
        <div>
          <h1 className="text-2xl font-bold text-[#181818] font-sans">Customers</h1>
          <p className="text-sm text-[#706E6B] mt-1">{customers.length} customer(s) · Stage 00 Foundation</p>
        </div>
        <button onClick={() => setShowCreate(true)}
          className="flex items-center gap-2 px-4 py-2 rounded-lg bg-[#0176D3] text-white text-sm font-semibold hover:opacity-90 transition-opacity">
          <Plus className="w-4 h-4" /> New Customer
        </button>
      </div>

      <div className="space-y-3">
        {customers.map(c => (
          <div key={c.id} onClick={() => customerApi.getCustomer(c.id).then(r => setSelected(r.data))}
            className="flex items-center gap-4 p-5 bg-[#F3F3F3] border border-[#E5E5E5] rounded-xl hover:border-[#0176D3]/40 cursor-pointer card-hover transition-all">
            <div className="w-10 h-10 rounded-full bg-gradient-to-br from-[#0176D3]/20 to-[#014486]/20 border border-[#0176D3]/30 flex items-center justify-center flex-shrink-0">
              <span className="text-sm font-bold text-[#0176D3]">{c.name.charAt(0)}</span>
            </div>
            <div className="flex-1 min-w-0">
              <div className="flex items-center gap-2">
                <h3 className="text-sm font-semibold text-[#181818]">{c.name}</h3>
                <span className="text-xs font-mono text-[#706E6B]">{c.customer_number}</span>
              </div>
              <div className="flex items-center gap-4 mt-1 text-xs text-[#706E6B]">
                {c.email && <span className="flex items-center gap-1"><Mail className="w-3 h-3" />{c.email}</span>}
                {c.phone && <span className="flex items-center gap-1"><Phone className="w-3 h-3" />{c.phone}</span>}
              </div>
            </div>
            <ChevronRight className="w-4 h-4 text-[#706E6B]" />
          </div>
        ))}
        {customers.length === 0 && (
          <div className="text-center py-12 bg-[#F3F3F3] border border-dashed border-[#E5E5E5] rounded-xl">
            <Users className="w-8 h-8 text-[#444444] mx-auto mb-3" />
            <p className="text-sm text-[#706E6B]">No customers yet</p>
          </div>
        )}
      </div>

      {showCreate && (
        <Modal title="New Customer" onClose={() => setShowCreate(false)}>
          <CustomerForm onSuccess={() => { setShowCreate(false); load(); }} />
        </Modal>
      )}

      {selected && (
        <Modal title={selected.name} onClose={() => setSelected(null)}>
          <div className="space-y-2">
            {[["Customer #", selected.customer_number], ["Email", selected.email || "—"], ["Phone", selected.phone || "—"], ["Status", selected.status]].map(([k, v]) => (
              <div key={k} className="flex justify-between p-2.5 rounded-lg bg-[#F3F3F3] border border-[#EEF4FF]">
                <span className="text-xs font-mono text-[#706E6B]">{k}</span>
                <span className="text-sm text-[#181818]">{v}</span>
              </div>
            ))}
            {(selected.contacts || []).length > 0 && (
              <div className="mt-4">
                <p className="text-xs font-mono text-[#706E6B] uppercase tracking-widest mb-2">Contacts</p>
                {selected.contacts.map(c => (
                  <div key={c.id} className="p-2.5 rounded-lg bg-[#F3F3F3] border border-[#EEF4FF] mb-2">
                    <p className="text-sm text-[#181818]">{c.name} {c.is_primary && <span className="text-[10px] text-[#0176D3]">Primary</span>}</p>
                    <p className="text-xs text-[#706E6B]">{c.email} · {c.phone}</p>
                  </div>
                ))}
              </div>
            )}
          </div>
        </Modal>
      )}
    </div>
  );
}

function CustomerForm({ onSuccess }) {
  const [form, setForm] = useState({ name: "", email: "", phone: "", company: "" });
  const [loading, setLoading] = useState(false);
  const submit = async (e) => {
    e.preventDefault(); setLoading(true);
    try { await customerApi.createCustomer(form); toast.success("Customer created"); onSuccess(); }
    catch (err) { toast.error(err?.response?.data?.detail || "Failed"); }
    finally { setLoading(false); }
  };
  return (
    <form onSubmit={submit} className="space-y-3">
      {[["name","Name","Sharma Family",true],["email","Email","sharma@example.com"],["phone","Phone","+91-9876543210"],["company","Company",""]].map(([k,l,p,req]) => (
        <div key={k}>
          <label className="block text-xs font-mono text-[#706E6B] uppercase tracking-widest mb-1">{l}{req && " *"}</label>
          <input value={form[k]} onChange={e=>setForm({...form,[k]:e.target.value})} placeholder={p} required={req}
            className="w-full px-3 py-2 rounded-lg bg-[#F3F3F3] border border-[#E5E5E5] text-[#181818] text-sm placeholder:text-[#706E6B] focus:outline-none focus:border-[#0176D3] transition-colors" />
        </div>
      ))}
      <button type="submit" disabled={loading}
        className="w-full py-2.5 rounded-lg bg-[#0176D3] text-white text-sm font-semibold hover:opacity-90 transition-opacity disabled:opacity-50 mt-2">
        {loading ? <Loader2 className="w-4 h-4 animate-spin mx-auto" /> : "Create Customer"}
      </button>
    </form>
  );
}

function Modal({ title, onClose, children }) {
  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-black/40 p-4">
      <div className="w-full max-w-md bg-[#F3F3F3] border border-[#E5E5E5] rounded-2xl shadow-2xl p-6 max-h-[80vh] overflow-y-auto">
        <div className="flex items-center justify-between mb-5">
          <h3 className="text-base font-semibold text-[#181818] font-sans">{title}</h3>
          <button onClick={onClose} className="text-[#706E6B] hover:text-[#181818]"><X className="w-4 h-4" /></button>
        </div>
        {children}
      </div>
    </div>
  );
}
